import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, TriangleAlert } from "lucide-react";
import { useIsMobile, reveal } from "@/lib/motion";
import { PHI, TYPE, GLOW, glassReveal } from "@/lib/glass";

const TRACK_SRC = `${process.env.PUBLIC_URL || ""}/audio/the-lotus-paradox.mp3`;

function fmt(s) {
  if (!s || Number.isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// The Lotus Paradox™ — a closing audio testament, below the reel and the prayer.
export default function LotusParadox() {
  const mobile = useIsMobile();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    a.currentTime = pct * dur;
    setCur(pct * dur);
  };

  const pct = dur ? (cur / dur) * 100 : 0;

  return (
    <section
      data-testid="lotus-section"
      style={{ paddingLeft: PHI.s3, paddingRight: PHI.s3, paddingTop: PHI.s5, paddingBottom: PHI.s6 }}
    >
      <div className="mx-auto max-w-[47rem]">
        <div aria-hidden="true" className="gold-hairline" style={{ marginBottom: PHI.s5 }} />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          {...glassReveal(reveal(mobile, { duration: 1.1 }))}
        >
          <h2
            data-testid="lotus-title"
            style={{ marginBottom: PHI.s3, fontSize: TYPE.h2, letterSpacing: "0.015em", lineHeight: 1.15, textShadow: GLOW.silver }}
            className="font-display font-light text-[#F5F5F7]"
          >
            The Lotus Paradox
            <sup className="align-super text-[0.4em]">™</sup>
          </h2>

          <div
            data-testid="lotus-warning"
            className="mb-8 flex items-start gap-3 border-l border-[#8E8E93]/40 pl-4"
          >
            <TriangleAlert size={16} className="mt-1 shrink-0 text-[#84DFF4]" />
            <p style={{ fontSize: TYPE.body, lineHeight: 1.7 }} className="font-light text-[#F5F5F7]/70">
              <span className="mr-2 text-xs font-normal uppercase tracking-[3px] text-[#8E8E93]">A warning</span>
              This light came from darkness. Listen knowing where it was forged.
            </p>
          </div>

          {/* player */}
          <div className="flex items-center gap-5 rounded-2xl border border-[#8E8E93]/15 bg-white/[0.02] px-5 py-5 backdrop-blur-sm">
            <button
              type="button"
              data-testid="lotus-play"
              onClick={toggle}
              aria-label={playing ? "Pause" : "Play"}
              data-cursor
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#84DFF4]/60 text-[#84DFF4] transition-colors duration-300 hover:bg-[#84DFF4] hover:text-[#030305]"
            >
              {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className="mb-3 truncate text-sm font-light tracking-[0.2em] text-[#F5F5F7]/80">
                The Lotus Paradox<sup className="align-super text-[0.5em]">™</sup>
              </p>
              <div
                data-testid="lotus-progress"
                onClick={seek}
                className="group relative h-1.5 w-full cursor-pointer rounded-full bg-[#F5F5F7]/10"
              >
                <div
                  className="absolute left-0 top-0 h-full rounded-full bg-[#84DFF4]"
                  style={{ width: `${pct}%` }}
                />
                <div
                  className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-[#84DFF4] opacity-0 shadow-[0_0_10px_rgba(132,223,244,0.6)] transition-opacity duration-200 group-hover:opacity-100"
                  style={{ left: `calc(${pct}% - 6px)` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[11px] font-light tabular-nums text-[#F5F5F7]/45">
                <span data-testid="lotus-current">{fmt(cur)}</span>
                <span data-testid="lotus-duration">{fmt(dur)}</span>
              </div>
            </div>
          </div>

          <audio
            ref={audioRef}
            data-testid="lotus-audio"
            src={TRACK_SRC}
            preload="metadata"
            onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
            onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
            onEnded={() => {
              setPlaying(false);
              setCur(0);
            }}
          />
        </motion.div>
      </div>
    </section>
  );
}
